import type { ComputedRef } from 'vue';

import { ref, unref } from 'vue';
import { toast } from 'vue3-toastify';

import capitalMarketsService from '../services/capitalMarketsService';
import { downloadBlobAsFile, openBlobInNewTab } from '../utils/fileDownload';

interface Document {
  file_uri: string;
}

interface UseDocumentActionsOptions {
  document: ComputedRef<Document | null> | Document | null | undefined;
  fileNameFallback?: ComputedRef<number | string> | number | string;
  fileNamePrefix?: string;
  financingId: number;
}

const MESSAGES = {
  DOCUMENT_NOT_AVAILABLE: 'Documento não disponível',
  DOWNLOAD_ERROR: 'Erro ao baixar documento. Tente novamente.',
  DOWNLOAD_SUCCESS: 'Documento baixado com sucesso!',
  VIEW_ERROR: 'Erro ao visualizar documento. Tente novamente.',
} as const;

export default function useDocumentActions({
  document,
  fileNameFallback,
  fileNamePrefix = 'CCB',
  financingId,
}: UseDocumentActionsOptions) {
  const isDownloading = ref(false);
  const isViewing = ref(false);

  const getDocument = (): Document | null => {
    const doc = unref(document);
    return doc || null;
  };

  const generateFileName = (extension = 'pdf'): string => {
    const fallback = unref(fileNameFallback) || financingId;
    return `${fileNamePrefix}_${fallback}.${extension}`;
  };

  const downloadDocument = async (): Promise<void> => {
    const doc = getDocument();
    if (!doc) {
      toast.error(MESSAGES.DOCUMENT_NOT_AVAILABLE);
      return;
    }

    try {
      isDownloading.value = true;
      const blob = await capitalMarketsService.downloadContractFile(
        financingId,
        doc.file_uri,
      );

      downloadBlobAsFile(blob, generateFileName());
      toast.success(MESSAGES.DOWNLOAD_SUCCESS);
    } catch {
      toast.error(MESSAGES.DOWNLOAD_ERROR);
    } finally {
      isDownloading.value = false;
    }
  };

  const viewDocument = async (): Promise<void> => {
    const doc = getDocument();
    if (!doc) {
      toast.error(MESSAGES.DOCUMENT_NOT_AVAILABLE);
      return;
    }

    try {
      isViewing.value = true;
      const blob = await capitalMarketsService.downloadContractFile(
        financingId,
        doc.file_uri,
      );

      openBlobInNewTab(blob);
    } catch {
      toast.error(MESSAGES.VIEW_ERROR);
    } finally {
      isViewing.value = false;
    }
  };

  return {
    downloadDocument,
    isDownloading,
    isViewing,
    viewDocument,
  };
}
